/**
 * @license Copyright (c) 2023-2025, CKSource Holding sp. z o.o. All rights reserved.
 * For licensing, see LICENSE.md.
 */

import { useEffect, useMemo, useState } from 'react';

export function useDimensions( element: HTMLElement | null ): { width: number; height: number; hasOverflowX: boolean } {
	const [ width, setWidth ] = useState( 0 );
	const [ height, setHeight ] = useState( 0 );
	const [ hasOverflowX, setHasOverflowX ] = useState( false );

	useEffect( () => {
		if ( !element ) {
			return;
		}

		const observer = new ResizeObserver( () => {
			const { width, height } = window.getComputedStyle( element );
			const widthAsNumber = Math.ceil( parseFloat( width ) );
			const heightAsNumber = Math.ceil( parseFloat( height ) );

			const elementScrollWidth = element.scrollWidth || 0;
			const hasOverflowX = widthAsNumber && elementScrollWidth && widthAsNumber < elementScrollWidth;

			setWidth( widthAsNumber );
			setHeight( heightAsNumber );
			setHasOverflowX( !!hasOverflowX );
		} );

		observer.observe( element );

		return () => observer.disconnect();
	}, [ element ] );

	return useMemo( () => ( { width, height, hasOverflowX } ), [ width, height, hasOverflowX ] );
}
